import { DynamicFormRequiredType, fieldValidationErrorMessage, type DynamicFormSettings } from '@myosh/odin-components';
import * as yup from 'yup';
import type { CombinedFieldType, ExtendedDynamicFormField } from '../../../../@types/forms';
import i18next from '../../../../i18n';
import type { FormBuilderField, FormBuilderFormSettings } from '../../../../redux/slices/form-builder-slice';
import { dataSettings } from '../../../form-builder/util/common.util';

export const AIFieldFormSettings: DynamicFormSettings = {
  id: 'AIFieldSettingsGroup',
  caption: '',
  fields: [
    {
      id: 'buttonCaption',
      fieldType: 'CUSTOM',
      customFieldType: 'TEXTFIELD',
      fieldName: 'buttonCaption',
      caption: i18next.t('button-caption'),
      helpMessage: 'Caption for the AI action button',
      required: DynamicFormRequiredType.True,
      permissions: { edit: true, read: true },
      position: 35,
      validationFunc: yup
        .string()
        .required(fieldValidationErrorMessage(i18next.t('button-caption')))
        .max(10, `${i18next.t('max-length-message', { maxLength: 10 })}`),
    },
    {
      id: 'aiPrompt',
      fieldType: 'CUSTOM',
      customFieldType: 'TEXTAREA',
      fieldName: 'aiPrompt',
      caption: i18next.t('extra-prompt'),
      helpMessage: 'Additional prompt to be sent to the AI',
      required: DynamicFormRequiredType.True,
      permissions: { edit: true, read: true },
      dynamicProperties: {
        rows: 10,
      },
      position: 40,
      validationFunc: yup
        .string()
        .required(fieldValidationErrorMessage(i18next.t('extra-prompt')))
        .max(2000, `${i18next.t('max-length-message', { maxLength: 2000 })}`),
    },
    {
      id: 'aiInputFieldIds',
      fieldType: 'DROPDOWNMULTISELECT',
      fieldName: 'aiInputFieldIds',
      caption: i18next.t('input-fields'),
      dataSettings: dataSettings,
      required: DynamicFormRequiredType.True,
      permissions: { edit: true, read: true },
      position: 45,
    },
    {
      id: 'aiOutputFieldIds',
      fieldType: 'DROPDOWNMULTISELECT',
      fieldName: 'aiOutputFieldIds',
      caption: i18next.t('output-fields'),
      dataSettings: dataSettings,
      required: DynamicFormRequiredType.True,
      permissions: { edit: true, read: true },
      position: 50,
    },
    {
      id: 'includeAttachment',
      fieldType: 'CHECKBOX',
      fieldName: 'includeAttachment',
      caption: i18next.t('include-attachments', 'Include attachments'),
      helpMessage:
        'When enabled, PDF documents and images attached to the record will be included as context for AI generation.',
      required: DynamicFormRequiredType.False,
      permissions: { edit: true, read: true },
      position: 25,
    },
  ],
};

/**
 * Gets all fields from a form configuration
 *
 * @param formConfig The form configuration to search
 * @param filterFieldTypes Optional array of field types to filter by
 *
 * @returns Array of fields from the form config
 */
export const getAllFormFields = (
  formConfig: FormBuilderFormSettings,
  filterFieldTypes?: CombinedFieldType[]
): ExtendedDynamicFormField[] => {
  const result: ExtendedDynamicFormField[] = [];

  if (!formConfig?.fieldGroups) return result;

  const fieldGroups = formConfig.fieldGroups;
  for (let i = 0, len = fieldGroups.length; i < len; i++) {
    const fields = fieldGroups[i].fields || [];

    for (let j = 0, fieldsLen = fields.length; j < fieldsLen; j++) {
      const field = fields[j];

      // Skip if we have a filter and this field type is not in the filter
      if (filterFieldTypes) {
        let matchesFilter = false;
        for (let k = 0, filterLen = filterFieldTypes.length; k < filterLen; k++) {
          if ((field.fieldType as CombinedFieldType) === filterFieldTypes[k]) {
            matchesFilter = true;
            break;
          }
        }
        if (!matchesFilter) continue;
      }

      result.push(field as ExtendedDynamicFormField);
    }
  }

  return result;
};

const listOfAllowedInputOutputFieldTypes: CombinedFieldType[] = [
  'TEXTFIELD',
  'TEXTAREA',
  'RICHTEXTAREA',
  'HISTORY',
  'OPTIONGROUP',
  'MULTISELECTCHECKBOX',
];

export const getAIFieldData = (field: FormBuilderField, allFormFields?: ExtendedDynamicFormField[]) => {
  if (!allFormFields) return [];

  const inputOutputFormFields = allFormFields.map((field) => ({
    text: `${field.fieldCaption.translations[0].value} [${field.fieldType}]`,
    value: field.id,
  }));

  const aiInputFieldIdsArray = Array.isArray(field.aiInputFieldIds) ? field.aiInputFieldIds : [];
  const aiOutputFieldIdsArray = Array.isArray(field.aiOutputFieldIds) ? field.aiOutputFieldIds : [];

  return {
    aiPrompt: field.aiPrompt ?? '',
    buttonCaption: field?.buttonCaption ? field.buttonCaption.translations?.[0]?.value : undefined,
    aiInputFieldIds:
      inputOutputFormFields.length > 0
        ? inputOutputFormFields.filter((item) => aiInputFieldIdsArray?.includes(item.value))
        : undefined,
    aiOutputFieldIds:
      inputOutputFormFields.length > 0
        ? inputOutputFormFields.filter((item) => aiOutputFieldIdsArray?.includes(item.value))
        : undefined,
    includeAttachment: field.includeAttachment ?? false,
  };
};

export const getAiFieldOptions = (formData: FormBuilderFormSettings) =>
  getAllFormFields(formData, listOfAllowedInputOutputFieldTypes).map((field) => ({
    text: `${field.fieldCaption.translations[0].value} [${field.fieldType}]`,
    value: field.id,
  }));
